/**
 * Custom-styled dropdown that matches the dark theme. Drop-in replacement
 * for `<select>` everywhere in the app — supports keyboard navigation
 * (arrows / Home / End / Enter / Escape / type-to-jump), click-outside
 * close, and an optional `renderOption` slot for icon + sublabel layouts.
 *
 * Intentionally not a full combobox: it's single-select, doesn't allow
 * typing free-form text, and renders into the normal DOM flow rather than
 * a portal (the menu uses absolute positioning relative to the trigger).
 * That keeps it simple and good enough for our lobby/options surfaces.
 */

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export interface SelectOption<T extends string | number> {
  value: T;
  label: string;
  /** Optional secondary line shown beneath the label in the menu. */
  sublabel?: string;
  /** Optional leading visual (emoji, glyph, mini-svg). */
  icon?: React.ReactNode;
  disabled?: boolean;
}

export interface SelectProps<T extends string | number> {
  value: T;
  options: SelectOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  /** Optional aria-label / tooltip. */
  title?: string;
  /** Inline width override for the trigger. */
  width?: number | string;
  /** Extra className on the trigger. */
  className?: string;
  /** Compact = smaller padding/font; useful in tight rows. */
  compact?: boolean;
}

export function Select<T extends string | number>({
  value,
  options,
  onChange,
  disabled,
  title,
  width,
  className,
  compact,
}: SelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [activeIdx, setActiveIdx] = useState(() =>
    Math.max(0, options.findIndex((o) => o.value === value))
  );
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const typedRef = useRef<{ buffer: string; until: number }>({ buffer: "", until: 0 });
  const listboxId = useId();

  const current = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value]
  );

  // Reset highlight + decide menu direction when reopened. We flip upward
  // when there isn't ~220px below the trigger — this matters inside the
  // in-game `.game-view` which clips overflow, and on phones where the
  // virtual keyboard or system bar can crowd the bottom.
  useEffect(() => {
    if (open) {
      const idx = options.findIndex((o) => o.value === value);
      setActiveIdx(idx >= 0 ? idx : 0);
      const trigger = triggerRef.current;
      if (trigger) {
        const rect = trigger.getBoundingClientRect();
        const below = window.innerHeight - rect.bottom;
        const menuMin = Math.min(220, options.length * 36 + 8);
        setOpenUpward(below < menuMin && rect.top > below);
      }
    }
  }, [open, options, value]);

  // Click outside closes.
  useEffect(() => {
    if (!open) return undefined;
    function onDocPointer(e: PointerEvent) {
      const root = rootRef.current;
      if (!root) return;
      if (e.target instanceof Node && root.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener("pointerdown", onDocPointer);
    return () => document.removeEventListener("pointerdown", onDocPointer);
  }, [open]);

  // Scroll the active row into view as the user arrows through.
  useLayoutEffect(() => {
    if (!open) return;
    const menu = menuRef.current;
    if (!menu) return;
    const row = menu.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`);
    if (row) row.scrollIntoView({ block: "nearest" });
  }, [open, activeIdx]);

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  const commit = useCallback(
    (idx: number) => {
      const opt = options[idx];
      if (!opt || opt.disabled) return;
      onChange(opt.value);
      close();
    },
    [options, onChange, close]
  );

  function moveActive(delta: number) {
    if (options.length === 0) return;
    let i = activeIdx;
    for (let step = 0; step < options.length; step++) {
      i = (i + delta + options.length) % options.length;
      if (!options[i]?.disabled) {
        setActiveIdx(i);
        return;
      }
    }
  }

  function onTriggerKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
    }
  }

  function onMenuKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      moveActive(1);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      moveActive(-1);
      return;
    }
    if (e.key === "Home") {
      e.preventDefault();
      setActiveIdx(0);
      return;
    }
    if (e.key === "End") {
      e.preventDefault();
      setActiveIdx(options.length - 1);
      return;
    }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      commit(activeIdx);
      return;
    }
    if (e.key === "Tab") {
      close();
      return;
    }
    // Type-to-jump: accumulate keystrokes within a 700ms window.
    if (e.key.length === 1) {
      const now = Date.now();
      const t = typedRef.current;
      if (now > t.until) t.buffer = "";
      t.buffer += e.key.toLowerCase();
      t.until = now + 700;
      const match = options.findIndex((o) =>
        o.label.toLowerCase().startsWith(t.buffer)
      );
      if (match >= 0) setActiveIdx(match);
    }
  }

  const triggerClass = [
    "ui-select-trigger",
    compact ? "compact" : "",
    open ? "open" : "",
    disabled ? "disabled" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="ui-select" ref={rootRef} style={width != null ? { width } : undefined}>
      <button
        type="button"
        ref={triggerRef}
        className={triggerClass}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-disabled={disabled || undefined}
        title={title}
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={onTriggerKeyDown}
      >
        <span className="ui-select-value">
          {current?.icon != null && <span className="ui-select-icon">{current.icon}</span>}
          <span className="ui-select-label-text">{current?.label ?? ""}</span>
        </span>
        <span className="ui-select-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div
          // Callback ref also moves focus into the menu so keyboard handling works.
          ref={(el) => {
            menuRef.current = el;
            if (el && document.activeElement !== el) el.focus();
          }}
          id={listboxId}
          role="listbox"
          tabIndex={-1}
          className={`ui-select-menu${openUpward ? " upward" : ""}`}
          onKeyDown={onMenuKeyDown}
        >
          {options.map((o, i) => {
            const isActive = i === activeIdx;
            const isSelected = o.value === value;
            return (
              <div
                key={String(o.value)}
                data-idx={i}
                role="option"
                aria-selected={isSelected}
                aria-disabled={o.disabled || undefined}
                className={[
                  "ui-select-option",
                  isActive ? "active" : "",
                  isSelected ? "selected" : "",
                  o.disabled ? "disabled" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onPointerEnter={() => setActiveIdx(i)}
                onPointerDown={(e) => {
                  // Prevent the document pointerdown listener from closing us
                  // before the click commits.
                  e.preventDefault();
                  commit(i);
                }}
              >
                {o.icon != null && <span className="ui-select-icon">{o.icon}</span>}
                <span className="ui-select-option-text">
                  <span className="ui-select-option-label">{o.label}</span>
                  {o.sublabel && (
                    <span className="ui-select-option-sublabel">{o.sublabel}</span>
                  )}
                </span>
                {isSelected && <span className="ui-select-check" aria-hidden="true">✓</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
